import { IonIcon } from '@ionic/react';
import { bookmarkOutline, trashOutline } from 'ionicons/icons';
import { useCallback, useState } from 'react';
import NFitHistogramChart from '../components/Chart/nfit-histogram';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { setAnnotation } from '../features/selected-scan/selected-scan-slice';
import { replaceHistory } from '../features/scan-results/scan-result-slice';
import { updateUserAnnotation } from '../services/resource-server/resource-server';
import { formatDateForAnnotation, formatElapsedTime, formatTimeOfDay } from '../utilities/display-formats';
import { computeFatigueHours } from '../utilities/fatigue-detector';
import { useCobraDelta } from '../hooks/use-cobra-delta';
import { ReactComponent as SleepIcon } from '../assets/sleep.svg';
import { ReactComponent as StressIcon } from '../assets/stress.svg';

const RISK_COLORS = [
    'rgb(178,61,44)',
    'rgb(178,100,100)',
    'rgb(110,91,90)',
    'rgb(102,102,102)',
    'rgb(79,100,87)',
    'rgb(37,110,69)',
    'rgb(44,140,61)',
];

const CLOCK_LABEL_HOURS = [0, 6, 12, 18];
const CLOCK_CENTER = { x: 85, y: 85 };
const CLOCK_FACE_RADIUS = 42;

function pointOnClock(hour: number, radius: number) {
    const angle = (hour / 24) * 2 * Math.PI;
    return {
        x: CLOCK_CENTER.x + radius * Math.sin(angle),
        y: CLOCK_CENTER.y - radius * Math.cos(angle),
    };
}

function hourOfDay(date: string) {
    const d = new Date(date);
    return d.getHours() + d.getMinutes() / 60;
}

const OUTCOME_COLORS = {
    credit: '#0E9648',
    debit: '#F0A06D',
};

function describeWedge(fromHour: number, toHour: number, radius: number) {
    const forwardSweep = ((toHour - fromHour) % 24 + 24) % 24;
    if (forwardSweep === 0) return null;

    // always take the shorter arc between the two hands, regardless of
    // which direction is "forward in time"
    const sweepFlag = forwardSweep <= 12 ? 1 : 0;

    const start = pointOnClock(fromHour, radius);
    const end = pointOnClock(toHour, radius);

    return `M ${CLOCK_CENTER.x},${CLOCK_CENTER.y} L ${start.x},${start.y} A ${radius},${radius} 0 0,${sweepFlag} ${end.x},${end.y} Z`;
}

const NIGHT_WEDGE_PATH = describeWedge(21, 6, CLOCK_FACE_RADIUS);

function TwentyFourHourClock({ date, previousDate }: { date: string | null, previousDate: string | null }) {
    const primaryHour = date !== null ? hourOfDay(date) : null;
    const previousHour = previousDate !== null ? hourOfDay(previousDate) : null;

    const previousHandPoint = previousHour !== null ? pointOnClock(previousHour, 34) : null;
    const primaryHandTip = primaryHour !== null ? pointOnClock(primaryHour, 58) : null;
    const primaryLabelPoint = primaryHour !== null ? pointOnClock(primaryHour, 62) : null;

    const wedgePath = previousHour !== null && primaryHour !== null
        ? describeWedge(previousHour, primaryHour, CLOCK_FACE_RADIUS)
        : null;
    const isEarlierInDay = previousHour !== null && primaryHour !== null && primaryHour < previousHour;
    const wedgeColor = isEarlierInDay ? OUTCOME_COLORS.credit : OUTCOME_COLORS.debit;

    return (
        <svg className="wakefulnessClock" viewBox="0 0 170 170" width="110" height="110">
            {NIGHT_WEDGE_PATH && (
                <path d={NIGHT_WEDGE_PATH} fill="#D6D6D6" fillOpacity="0.5" />
            )}
            {wedgePath && (
                <path d={wedgePath} fill={wedgeColor} fillOpacity="0.2" />
            )}
            <circle cx={CLOCK_CENTER.x} cy={CLOCK_CENTER.y} r={CLOCK_FACE_RADIUS} fill="none" stroke="#D6D6D6" strokeWidth="2" />
            {Array.from({ length: 24 }, (_, hour) => {
                const isLabeled = CLOCK_LABEL_HOURS.includes(hour);
                const outer = pointOnClock(hour, CLOCK_FACE_RADIUS);
                const inner = pointOnClock(hour, isLabeled ? CLOCK_FACE_RADIUS - 7 : CLOCK_FACE_RADIUS - 4);
                return (
                    <line
                        key={hour}
                        x1={inner.x} y1={inner.y}
                        x2={outer.x} y2={outer.y}
                        stroke="#4D4D4D"
                        strokeWidth={isLabeled ? 1.5 : 0.75}
                    />
                );
            })}
            {CLOCK_LABEL_HOURS.map(hour => {
                const { x, y } = pointOnClock(hour, CLOCK_FACE_RADIUS - 12);
                return (
                    <text key={hour} x={x} y={y} fontSize="9" textAnchor="middle" dominantBaseline="middle" fill="#4D4D4D">
                        {hour}
                    </text>
                );
            })}
            {previousHandPoint && (
                <line
                    x1={CLOCK_CENTER.x} y1={CLOCK_CENTER.y}
                    x2={previousHandPoint.x} y2={previousHandPoint.y}
                    stroke="#C4C4C4" strokeWidth="2.5" strokeLinecap="round"
                />
            )}
            {primaryHandTip && (
                <>
                    <line
                        x1={CLOCK_CENTER.x} y1={CLOCK_CENTER.y}
                        x2={primaryHandTip.x} y2={primaryHandTip.y}
                        stroke="#1476BC" strokeWidth="2.5" strokeLinecap="round"
                    />
                    {primaryLabelPoint && date && (
                        <text
                            x={primaryLabelPoint.x} y={primaryLabelPoint.y}
                            fontSize="11" fontWeight="bold"
                            textAnchor="end"
                            dominantBaseline="middle"
                            fill="#1476BC"
                        >
                            {formatTimeOfDay(date)}
                        </text>
                    )}
                </>
            )}
            <circle cx={CLOCK_CENTER.x} cy={CLOCK_CENTER.y} r="2.5" fill="#1476BC" />
        </svg>
    );
}

function RiskBar({ value }: { value: number | null }) {
    return (
        <div className="risk-factor-bar-container">
            {RISK_COLORS.map((color, i) => {
                const active = value !== null && value === i;
                return (
                    <div
                        key={i}
                        className="risk-segment"
                        style={{ backgroundColor: color, opacity: value === null ? 1 : active ? 1 : 0.25 }}
                    />
                );
            })}
        </div>
    );
}

export default function Histogram() {
    const [selfReferencingScore, setSelfReferencingScore] = useState<boolean>(false);
    const [daysBack, setDaysBack] = useState<number>(365);
    const [startDate, setStartDate] = useState<string>(
        new Date(new Date().setDate(new Date().getDate() - 365)).toISOString()
    );

    const dispatch = useAppDispatch();
    const selectedScan = useAppSelector((state) => state.selectedScan.record);
    const [displayScore, setDisplayScore] = useState<number | null>(null);
    const [binColor, setBinColor] = useState<string>('#000000');

    const cobraDelta = useCobraDelta();
    const fatigueHours = cobraDelta !== null ? computeFatigueHours(cobraDelta.deltas) : null;
    const wakefulnessHours = fatigueHours !== null ? -fatigueHours : null;
    const wakefulnessLabel = wakefulnessHours !== null && wakefulnessHours < 0 ? 'debit' : 'credit';
    const elapsedTime = cobraDelta !== null ? formatElapsedTime(cobraDelta.previousDate, cobraDelta.primaryDate) : null;

    const handleStatsReady = useCallback(() => {}, []);
    const handleMostRecentScore = useCallback((score: number) => setDisplayScore(score), []);
    const handleMostRecentBinColor = useCallback((color: string) => setBinColor(color), []);

    const setStartParameter = (days: number) => {
        setDaysBack(days);
        const newDate = days === -1 ? new Date('1900-01-01') : new Date();
        if (days !== -1) newDate.setDate(new Date().getDate() - days);
        setStartDate(newDate.toISOString());
    };

    const trackEnteredAnnotation = (text: string) => {
        dispatch(setAnnotation(text));
    };

    const saveAnnotation = (text: string) => {
        if (selectedScan !== null) {
            const updatedScan = { ...selectedScan, annotation: text };
            dispatch(replaceHistory(updatedScan));
            const sendData = async () => {
                await updateUserAnnotation(selectedScan.id, text);
            };
            sendData();
        }
    };

    const deleteAnnotation = () => {
        dispatch(setAnnotation(''));
        saveAnnotation('');
    };

    return (
        <>
            <div className="drop-shadow-content-box nFitScores">
                <div className="controls">
                    <button className={daysBack === -1 ? "timePeriod selected" : "timePeriod"} onClick={() => setStartParameter(-1)}>all time</button>
                    <button className={daysBack === 365 ? "timePeriod selected" : "timePeriod"} onClick={() => setStartParameter(365)}>1y</button>
                    <button className={daysBack === 30 ? "timePeriod selected" : "timePeriod"} onClick={() => setStartParameter(30)}>1m</button>
                    <button className={daysBack === 7 ? "timePeriod selected" : "timePeriod"} onClick={() => setStartParameter(7)}>1wk</button>
                    <label><input type="checkbox" checked={selfReferencingScore} onChange={() => setSelfReferencingScore(!selfReferencingScore)} /> self-reference</label>
                </div>
                <div className="nFitGraph">
                    <NFitHistogramChart
                        selfReferencingScore={selfReferencingScore}
                        startDate={startDate}
                        onStatsReady={handleStatsReady}
                        onMostRecentScore={handleMostRecentScore}
                        onMostRecentBinColor={handleMostRecentBinColor}
                    />
                </div>
                <div className="manageScores">
                    <div className="overallScore">
                        <span className="overallScoreLabel">nFit score</span>
                        <div className="numericOverallScore" style={{ color: binColor }}>
                            {displayScore !== null ? displayScore.toFixed(2) : ''}
                        </div>
                    </div>
                    <div className="annotationForm">
                        <div className="annotationInfo">
                            <IonIcon icon={bookmarkOutline} size="medium"></IonIcon> Annotation {formatDateForAnnotation(selectedScan?.date)}
                        </div>
                        <div className="annotationEntry">
                            <textarea
                                className="annotation"
                                title="" placeholder=""
                                onChange={event => trackEnteredAnnotation(event.target.value)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' && event.shiftKey === false) {
                                        event.preventDefault();
                                        saveAnnotation(selectedScan ? selectedScan.annotation : '');
                                    }
                                }}
                                value={selectedScan ? selectedScan.annotation : ''}
                            ></textarea>
                            <button className="trash" title="Delete" onClick={() => deleteAnnotation()}>
                                <IonIcon icon={trashOutline} size="medium"></IonIcon>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="drop-shadow-content-box">
                <div className="sectionTitle">AFWERX Risk Factors Survey</div>
                <div className="risk-factor-row">
                    <SleepIcon className="risk-factor-icon" />
                    <RiskBar value={selectedScan?.riskFactors?.last_nights_sleep ?? null} />
                </div>
                <div className="risk-factor-row">
                    <StressIcon className="risk-factor-icon" />
                    <RiskBar value={selectedScan?.riskFactors?.current_stress ?? null} />
                </div>
            </div>
            <div className="drop-shadow-content-box wakefulnessBox">
                <div className="overallScore wakefulnessScore">
                    <span className="overallScoreLabel">USAARL Restedness</span>
                    <div className={`numericOverallScore ${wakefulnessLabel === 'credit' ? "up" : "down"}`}>
                        {wakefulnessHours !== null ? `${Math.abs(wakefulnessHours).toFixed(1)} hrs ${wakefulnessLabel}` : '—'}
                    </div>
                    {elapsedTime && <div className="wakefulnessElapsed">{elapsedTime} between scans</div>}
                </div>
                <TwentyFourHourClock
                    date={cobraDelta?.primaryDate ?? null}
                    previousDate={cobraDelta?.previousDate ?? null}
                />
            </div>
        </>
    );
}
